import { useMemo } from 'react';
import { useServicesQuery } from '@/services/api';
import { useAuth } from '@/services/auth';

const EMPTY_SKILLS: CopilotSkill[] = [];

export interface CopilotSkill {
  /** Human-readable name from frontmatter, or directory name as fallback. */
  name: string;
  /** Description from frontmatter, if present. */
  description: string;
  /** Directory slug, e.g. "weekly-report" */
  slug: string;
}

/**
 * Fetch copilot skills for the current account. Returns a lightweight list
 * suitable for the slash command menu — name, description, and slug only.
 */
export function useCopilotSkills(): CopilotSkill[] {
  const { account } = useAuth();

  const query = useServicesQuery(
    'listCopilotFiles',
    { params: { accountId: account.id } },
    {
      select: (data) =>
        data.files
          .filter((f) => /^skills\/[^/]+\/SKILL\.md$/.test(f.path))
          .map((f) => {
            const slug = f.path.replace(/^skills\//, '').replace(/\/SKILL\.md$/, '');
            const name = f.frontmatter?.name;
            return {
              name: typeof name === 'string' && name.trim().length > 0 ? name : slug,
              description: (f.frontmatter?.description as string) ?? '',
              slug,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name)),
    },
  );

  return query.data ?? EMPTY_SKILLS;
}

/** Convenience hook returning just the set of skill slugs (for highlighting). */
export function useCopilotSkillSlugs(): Set<string> {
  const skills = useCopilotSkills();
  return useMemo(() => new Set(skills.map((s) => s.slug)), [skills]);
}
